import { useState } from "react";

import { TFunction, useTranslation } from "@impero-com/common/utils/i18n";
import { emitNotification, TextToolState } from "@impero-com/ui";
import { Row } from "@tanstack/react-table";

import { postControlResultsRemind } from "api/generated/control-results/control-results";

import { ControlActivityTableRow } from "./types";

function isRemindable(row: ControlActivityTableRow): boolean {
    return (
        !row.archived &&
        row.workflowStatus !== "Completed" &&
        row.workflowStatus !== "CompletedAfterDueDate"
    );
}

function computeReminderToolState(
    t: TFunction,
    hasSelection: boolean,
    hasRemindable: boolean,
    hasPermission: boolean
): TextToolState {
    if (!hasSelection) {
        return {
            type: "DISABLED",
            reason: t("ControlActivitiesList_Tooltip_NoSelection"),
        };
    }
    if (!hasRemindable) {
        return {
            type: "DISABLED",
            reason: t("ControlActivitiesList_Tooltip_NotRemindable"),
        };
    }
    if (!hasPermission) {
        return {
            type: "DISABLED",
            reason: t("ControlActivitiesList_Tooltip_Remind_NoPermission"),
        };
    }
    return { type: "ENABLED" };
}

interface UseControlActivityReminderUtilsProps {
    selectedRows: Row<ControlActivityTableRow>[];
    deselectAll: () => void;
}

export function useControlActivityReminderUtils({
    selectedRows,
    deselectAll,
}: UseControlActivityReminderUtilsProps) {
    const { t } = useTranslation();
    const [reminderModalOpen, setReminderModalOpen] = useState(false);
    const [isReminding, setIsReminding] = useState(false);

    const remindableRows = selectedRows.filter((row) =>
        isRemindable(row.original)
    );

    const remindableIds = remindableRows
        .filter((row) => row.original.canEditProgram)
        .map((row) => row.original.activityId);
    const reminderToolState = computeReminderToolState(
        t,
        selectedRows.length > 0,
        remindableRows.length > 0,
        remindableIds.length > 0
    );

    const openReminderModal = () => setReminderModalOpen(true);
    const closeReminderModal = () => setReminderModalOpen(false);

    const handleRemind = async (customMessage?: string) => {
        setIsReminding(true);
        try {
            await postControlResultsRemind({
                controlResultIds: remindableIds,
                customMessage,
            });
            const count = remindableIds.length;
            emitNotification(
                "success",
                count === 1
                    ? t("ControlResult_Reminded1")
                    : `${count} ${t("ControlResult_RemindedN")}`
            );
            deselectAll();
            closeReminderModal();
        } catch {
            emitNotification("error", t("UnknownError"));
        } finally {
            setIsReminding(false);
        }
    };

    return {
        reminderToolState,
        reminderModalOpen,
        openReminderModal,
        closeReminderModal,
        handleRemind,
        isReminding,
    };
}
